/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/AddAPhoto";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import AddBoxIcon from "@mui/icons-material/AddBox";
import PreviewIcon from "@mui/icons-material/Preview";
import CButton from "../../../basecomp/components/CButton2";
import { CircularProgress } from "@mui/material";
import {
  fetchSizeOptions,
  fetchStickers,
  fetchPoOrMaterial,
  addSticker,
  updateSticker,
  deleteSticker,
  getStickerPdf,
  addAllSticker,
  fetchSlocOptions,
  changePreference,
  checkBinLocation,
  getBinLocationExport,
} from "../model/PrintingModel";
import { AddPrintKeys, PrintViewTypes } from "../../assets/VarConstants";
import {
  gblNameOrLocalNameCheck,
  ImageUpload,
} from "../../../image_upload/src/model/ImageUpload";
import {
  ImageRequired,
  imageSize,
  MaterialNumberRequired,
  MaxImageSelect,
  MaxImageUpload,
  maxSizePerFile,
} from "../../../image_upload/assets/Strings";
import { LibraryAdd, PlaylistAddCircleOutlined } from "@mui/icons-material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";

const EXPORT_PERMISSION = "export_bin_data";

const getPermissionData = () => {
  // 1) permissionData saved directly in localStorage as a JSON array
  try {
    const raw = localStorage.getItem("permissionData");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((p) => String(p).trim());
    }
  } catch (e) {}

  // 2) fallback: permissionData inside the JWT token payload
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const payload = JSON.parse(
        atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
      );
      if (Array.isArray(payload.permissionData)) {
        return payload.permissionData.map((p) => String(p).trim());
      }
    }
  } catch (e) {}

  return [];
};

const hasPermission = (name) => getPermissionData().includes(name);

const PrintingVM = () => {
  const navigate = useNavigate();
  const { handleToastOpen } = useToast();
  const { t } = useTranslation();
  const [isPrinting, setIsPrinting] = useState(false);
  const [poOptions, setPoOptions] = useState([]);
  const [matOptions, setMatOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [sLocOptions, setSLocOptions] = useState([]);
  const [stickerData, setStickerData] = useState({
    [AddPrintKeys.PO_KEY]: "",
    [AddPrintKeys.MAT_NO_KEY]: "",
    [AddPrintKeys.SIZE_KEY]: "",
    [AddPrintKeys.QUANTITY_KEY]: 1,
    [AddPrintKeys.STORAGE_LOC_KEY]: "",
  });
  const [selectedSize, setSelectedSize] = useState();
  const [selectedSLoc, setSelectedSLoc] = useState();
  const isTableEdit = useRef(false);
  const [buttonJson, setButtonJson] = useState({
    add: {
      text: "Add",
      tooltipText: "Add",
      isDisabled: false,
      src: <AddBoxIcon />,
    },
    add_all: {
      text: "Add All",
      tooltipText: "Add All",
      isDisabled: false,
      src: <LibraryAdd />,
    },
    print: {
      text: "Print",
      tooltipText: "Print",
      isDisabled: false,
      src: <LocalPrintshopIcon />,
    },
    view: {
      text: "View",
      tooltipText: "View",
      isDisabled: false,
      src: <PictureAsPdfIcon />,
    },
    ...(hasPermission(EXPORT_PERMISSION) && {
      export: {
        text: "Export",
        tooltipText: "Export Bin Location Data",
        isDisabled: false,
        src: <FileDownloadIcon />,
      },
    }),
  });
  const [openCarousel, setOpenCarousel] = useState(false);
  const [labelData, setLabelData] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [globalType, setGlobalType] = useState("");

  const [forceInput, setForceInput] = useState(false);
  const tableStickerDataGlobal = useRef({});

  // ---- busy state ----
  // isCheckingBin = UI only (spinner). Guards use their own refs.
  const [isCheckingBin, setIsCheckingBin] = useState(false);
  const isVerifyingRef = useRef(false); // Save-time verify in progress
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const isBusy = isCheckingBin || isSaving;

  // ---- live search while typing ----
  const debounceRef = useRef(null);
  const checkIdRef = useRef(0);
  const lastWarnedBinRef = useRef(""); // bin we already showed the popup for
  const acknowledgedBinRef = useRef(""); // bin the user confirmed in the popup

  // clear pending timer on unmount
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // ---- duplicate bin location popup state ----
  const labelDataRef = useRef([]);
  const [duplicateDialog, setDuplicateDialog] = useState({
    open: false,
    binLocation: "",
    materials: [],
  });
  const pendingSaveRef = useRef(null);
  const savedDuplicateIdsRef = useRef(new Set());
  labelDataRef.current = labelData; // always the latest rows

  const fetchAllStickers = async () => {
    try {
      const response = await fetchStickers();
      const updatedStickers = response.data.map((obj) => ({
        ...obj,
        tableMode: "T",
        // rows saved with a duplicate bin stay blue in CTable2
        other_materials_in_bin_flag: savedDuplicateIdsRef.current.has(obj.id)
          ? 1
          : obj.other_materials_in_bin_flag,
      }));
      setLabelData(updatedStickers);
    } catch (error) {
      setLabelData([]);
    }
  };

  useEffect(() => {
    if (stickerData[AddPrintKeys.PO_KEY] !== "") {
      handlePOEdit(stickerData[AddPrintKeys.PO_KEY]);
    }
  }, [stickerData[AddPrintKeys.PO_KEY]]);

  useEffect(() => {
    if (stickerData[AddPrintKeys.MAT_NO_KEY] !== "") {
      handleMaterialEdit(stickerData[AddPrintKeys.MAT_NO_KEY]);
    }
  }, [stickerData[AddPrintKeys.MAT_NO_KEY]]);

  useEffect(() => {
    const fetchSize = async () => {
      try {
        const response = await fetchSizeOptions();
        setSizeOptions(response.data);
      } catch (error) {
        handleToastOpen("error", error.message);
        setSizeOptions([]);
      }
    };
    const fetchSloc = async () => {
      try {
        const response = await fetchSlocOptions();
        setSLocOptions(response.data.options);
        if (response.data.storage_loc) {
          setSelectedSLoc(response.data.storage_loc);
          handleStickerDataEdit(AddPrintKeys.STORAGE_LOC_KEY)(
            response.data.storage_loc,
          );
        }
      } catch (error) {
        handleToastOpen("error", error.message);
        setSLocOptions([]);
      }
    };
    fetchSize();
    fetchSloc();
    fetchAllStickers();
  }, []);

  const normalizeBin = (v) => (v ?? "").toString().trim().toUpperCase();

  // Checks the bin value against the other rows in the table and the DB
  const getDuplicateMaterials = async (value, editingRow) => {
    const newBin = normalizeBin(value);
    if (!newBin) return [];

    const uiMaterials = labelDataRef.current
      .filter(
        (row) =>
          row.id !== editingRow.id && normalizeBin(row.bin_location) === newBin,
      )
      .map((row) => row.material_no);

    let dbMaterials = [];
    try {
      const response = await checkBinLocation(
        String(value).trim(),
        editingRow.material_no,
      );
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      dbMaterials = list
        .filter(
          (item) =>
            normalizeBin(item.bin_location) === newBin &&
            item.material_no !== editingRow.material_no,
        )
        .map((item) => item.material_no);
    } catch (error) {
      console.log("error while checking bin location", error);
    }

    return [...new Set([...uiMaterials, ...dbMaterials])];
  };

  const checkDuplicateBin = async (value) => {
    const checkId = checkIdRef.current;
    try {
      const materials = await getDuplicateMaterials(
        value,
        tableStickerDataGlobal.current,
      );

      // user typed again, or pressed Save, while this ran: ignore the result
      if (checkId !== checkIdRef.current) return;

      const bin = normalizeBin(value);
      if (materials.length === 0) {
        lastWarnedBinRef.current = "";
        return;
      }
      if (lastWarnedBinRef.current === bin) return; // already warned for this bin

      lastWarnedBinRef.current = bin;
      setDuplicateDialog({ open: true, binLocation: value, materials });
    } catch (error) {
      console.log("error while checking bin", error);
    } finally {
      if (checkId === checkIdRef.current) setIsCheckingBin(false);
    }
  };

  const handleTableEditClick = (key) => (value) => {
    tableStickerDataGlobal.current[key] = value;

    if (key === AddPrintKeys.BIN_LOC_KEY) {
      clearTimeout(debounceRef.current);
      checkIdRef.current += 1; // cancels any search already running

      if (!normalizeBin(value)) {
        setIsCheckingBin(false);
        lastWarnedBinRef.current = "";
        acknowledgedBinRef.current = "";
        return;
      }

      setIsCheckingBin(true); // spinner starts as soon as the user types
      debounceRef.current = setTimeout(() => checkDuplicateBin(value), 500);
    }
  };

  const handleDuplicateClose = () => {
    pendingSaveRef.current = null;
    setDuplicateDialog({ open: false, binLocation: "", materials: [] });
  };

  const handleDuplicateConfirm = async () => {
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;
    setDuplicateDialog({ open: false, binLocation: "", materials: [] });
    if (!pending) return;

    savedDuplicateIdsRef.current.add(pending.sticker.id); // keep row blue
    await saveEditedSticker(pending.data, pending.index, pending.sticker);
  };

  const saveEditedSticker = async (data, index, sticker) => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (sticker.bin_location?.includes(" ")) {
        handleToastOpen("error", "Bin location cannot contain spaces.");
        return;
      }
      const response = await updateSticker(
        sticker.id,
        sticker.material_no,
        sticker.bin_location === null ? "" : sticker.bin_location,
        sticker.bulk_location === null ? "" : sticker.bulk_location,
        sticker.print_qty,
      );
      const updatedData = response.data;
      isTableEdit.current = false;
      tableStickerDataGlobal.current = {};
      setLabelData((prev) => {
        const updated = [...prev];
        updated[index].tableMode = "T";
        updated[index].material_no = updatedData.material_no;
        updated[index].material_status = updatedData.material_status;
        updated[index].bin_location = updatedData.bin_location;
        updated[index].bin_loc_status = updatedData.bin_loc_status;
        updated[index].bulk_location = updatedData.bulk_location;
        updated[index].print_qty = updatedData.print_qty;
        return updated;
      });
      await fetchAllStickers();
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleEditClick = async (data, index) => {
    if (data.tableMode === "E") {
      // only block while a Save verify or save is really running
      if (isVerifyingRef.current || isSavingRef.current) return;

      // cancel the typing search, Save runs its own fresh check
      clearTimeout(debounceRef.current);
      checkIdRef.current += 1;

      const sticker = { ...tableStickerDataGlobal.current };

      isVerifyingRef.current = true;
      setIsCheckingBin(true);
      let duplicates = [];
      try {
        duplicates = await getDuplicateMaterials(sticker.bin_location, sticker);
      } catch (error) {
        console.log("error while verifying bin", error);
      } finally {
        isVerifyingRef.current = false;
        setIsCheckingBin(false); // always released
      }

      const bin = normalizeBin(sticker.bin_location);
      if (duplicates.length > 0 && acknowledgedBinRef.current !== bin) {
        pendingSaveRef.current = { data, index, sticker };
        setDuplicateDialog({
          open: true,
          binLocation: sticker.bin_location,
          materials: duplicates,
        });
        return;
      }

      if (duplicates.length > 0) {
        savedDuplicateIdsRef.current.add(sticker.id); // keep row blue
      } else {
        savedDuplicateIdsRef.current.delete(sticker.id);
      }
      await saveEditedSticker(data, index, sticker);
    } else {
      if (isTableEdit.current === false) {
        isTableEdit.current = true;
        lastWarnedBinRef.current = "";
        acknowledgedBinRef.current = "";
        tableStickerDataGlobal.current = { ...data, tableMode: "E" };
        setLabelData((prev) => {
          const updated = [...prev];
          updated[index].tableMode = "E";
          return updated;
        });
      }
    }
  };

  const handlePOEdit = async (value) => {
    try {
      if (value === "" || value === null) {
        setPoOptions([]);
        setMatOptions([]);
        setStickerData((prevStickerData) => ({
          ...prevStickerData,
          [AddPrintKeys.MAT_NO_KEY]: "",
        }));
      } else if (
        stickerData[AddPrintKeys.MAT_NO_KEY] === "" ||
        stickerData[AddPrintKeys.MAT_NO_KEY] === null
      ) {
        const response = await fetchPoOrMaterial(value, "");
        setMatOptions(response.data);
      }
    } catch (error) {
      handleToastOpen("error", error.message);
    }
  };

  const handleMaterialEdit = async (value, type) => {
    try {
      if (value === "" || value === null) {
        setPoOptions([]);
        setMatOptions([]);
        setStickerData((prevStickerData) => ({
          ...prevStickerData,
          [AddPrintKeys.PO_KEY]: "",
        }));
      } else if (stickerData[AddPrintKeys.PO_KEY] === "" && type === "input") {
        const response = await fetchPoOrMaterial("", value);
        setPoOptions(response.data);
      }
    } catch (error) {
      // handleToastOpen("error", error.message);
    }
  };

  const handleStickerDataEdit = (key) => (value, type) => {
    setStickerData((prevStickerData) => ({
      ...prevStickerData,
      [key]: typeof value === "string" && value !== null ? value.trim() : value,
    }));

    if (key === AddPrintKeys.MAT_NO_KEY) {
      handleMaterialEdit(value, type);
      if (type === "input") {
        setStickerData((prevStickerData) => ({
          ...prevStickerData,
          [AddPrintKeys.PO_KEY]: "",
        }));
      }
      setPoOptions([]);
      setForceInput(false);
    } else if (key === AddPrintKeys.PO_KEY) {
      handlePOEdit(value);
      setMatOptions([]);
      if (type === "input") {
        setStickerData((prevStickerData) => ({
          ...prevStickerData,
          [AddPrintKeys.MAT_NO_KEY]: "",
        }));
      }
    }
  };

  const handleSizeChange = (value) => {
    if (value === null || value === "") {
      handleStickerDataEdit(AddPrintKeys.SIZE_KEY)("");
    } else {
      handleStickerDataEdit(AddPrintKeys.SIZE_KEY)(value.id);
    }
  };

  const handleStorageLocationChange = (value) => {
    if (value === null || value === "") {
      handleStickerDataEdit(AddPrintKeys.STORAGE_LOC_KEY)("");
    } else {
      handleStickerDataEdit(AddPrintKeys.STORAGE_LOC_KEY)(value);
      changePreferenceFunc("storage_loc", value);
    }
  };

  const changePreferenceFunc = async (type, value) => {
    try {
      await changePreference(type, value);
    } catch (error) {
      console.log("error occoured in changing preference");
      console.log(error.response);
    }
  };

  const validateStickers = () => {
    if (
      stickerData[AddPrintKeys.MAT_NO_KEY] === "" ||
      stickerData[AddPrintKeys.SIZE_KEY] === "" ||
      stickerData[AddPrintKeys.QUANTITY_KEY] === "" ||
      stickerData[AddPrintKeys.STORAGE_LOC_KEY] === ""
    ) {
      handleToastOpen("error", "Please enter the required fields");
      return false;
    }

    return true;
  };

  const addStickerData = async () => {
    try {
      if (
        stickerData[AddPrintKeys.QUANTITY_KEY] > 500 ||
        stickerData[AddPrintKeys.QUANTITY_KEY] <= 0
      ) {
        handleToastOpen("error", "Quantity should be in between 1-500");
        return false;
      }
      const response = await addSticker(stickerData);
      handleToastOpen("success", response.data.message);
      await fetchAllStickers();
    } catch (error) {
      handleToastOpen("error", error.message);
    }
  };

  const addAllStickerData = async () => {
    try {
      const response = await addAllSticker(
        stickerData[AddPrintKeys.SIZE_KEY],
        stickerData[AddPrintKeys.PO_KEY],
        stickerData[AddPrintKeys.STORAGE_LOC_KEY],
      );
      handleToastOpen("success", response.data);
      await fetchAllStickers();
    } catch (error) {
      handleToastOpen("error", error.message);
    }
  };

  const deleteStickerData = async (id) => {
    try {
      const response = await deleteSticker(id);
      handleToastOpen("success", response.data.message);
      isTableEdit.current = false;
      tableStickerDataGlobal.current = {};
      lastWarnedBinRef.current = "";
      acknowledgedBinRef.current = "";
      await fetchAllStickers();
    } catch (error) {
      handleToastOpen("error", error.message);
    }
  };

  const getFormattedDateTime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 10);
    const currentTime = currentDate.toLocaleTimeString();
    return formattedDate + "_" + currentTime;
  };

  const pdfPrintView = async (id, stickerData, isPrint, type) => {
    try {
      setIsPrinting(true);
      const response = await getStickerPdf(id, stickerData, type, isPrint);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      if (isPrint) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `label_list_${getFormattedDateTime()}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
      setIsPrinting(false);
    } catch (error) {
      setIsPrinting(false);
      handleToastOpen("error", error.message);
    }
  };

  const exportEmptyBinLocations = async () => {
    try {
      setIsPrinting(true);

      const response = await getBinLocationExport();

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `empty_bin_locations_${getFormattedDateTime()}.xlsx`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      window.URL.revokeObjectURL(url);
      setIsPrinting(false);
    } catch (error) {
      setIsPrinting(false);
      handleToastOpen("error", error.message);
    }
  };

  const handelUserTyping = () => {
    setForceInput(true);
    setMatOptions([]);
    setStickerData((prevStickerData) => ({
      ...prevStickerData,
      [AddPrintKeys.MAT_NO_KEY]: "",
    }));
  };

  const handleButtonClick = (key) => {
    if (key === "add_all") {
      if (
        stickerData[AddPrintKeys.SIZE_KEY] !== "" &&
        stickerData[AddPrintKeys.PO_KEY] !== "" &&
        stickerData[AddPrintKeys.STORAGE_LOC_KEY] !== ""
      ) {
        addAllStickerData();
      } else {
        handleToastOpen(
          "error",
          "Either size , purchase order or storage_location is missing",
        );
      }
    } else if (key === "add") {
      const isValidated = validateStickers();
      if (isValidated) {
        addStickerData();
      }
    } else if (key === "delete_all") {
      deleteStickerData();
    } else if (key === "print") {
      const isValidated = validateStickers();
      if (isValidated) {
        pdfPrintView(
          undefined,
          stickerData,
          true,
          PrintViewTypes.PRINT_VIEW_DATA,
        );
      }
    } else if (key === "view") {
      const isValidated = validateStickers();
      if (isValidated) {
        pdfPrintView(
          undefined,
          stickerData,
          false,
          PrintViewTypes.PRINT_VIEW_DATA,
        );
      }
    } else if (key === "print_all") {
      pdfPrintView(undefined, undefined, true, PrintViewTypes.PRINT_VIEW_ALL);
    } else if (key === "export") {
      if (!hasPermission(EXPORT_PERMISSION)) {
        handleToastOpen("error", "You do not have permission to export");
        return;
      }
      exportEmptyBinLocations();
    }
  };

  const handelImageUpload = async (materialReq, selectedImages) => {
    if (!materialReq) {
      handleToastOpen("error", MaterialNumberRequired);
      return;
    }
    if (!selectedImages || selectedImages.length === 0) {
      handleToastOpen("error", ImageRequired);
      return;
    }
    if (selectedImages.length > 5) {
      handleToastOpen("error", MaxImageSelect);
      return;
    }
    // Validate each image size and total size
    let totalSize = 0;

    for (const image of selectedImages) {
      totalSize += image.size;

      if (image.size > maxSizePerFile) {
        handleToastOpen("error", imageSize);
        return;
      }
    }
    setIsPrinting(true);
    const email = localStorage.getItem("email");
    const response = await gblNameOrLocalNameCheck(email);
    setGlobalType(response.data.is_global);
    try {
      await ImageUpload(
        materialReq,
        response.data.is_global,
        selectedImages,
        handleToastOpen,
      );
    } catch (error) {
      console.log("we are in error", error);
    } finally {
      setIsPrinting(false); // Set loading to false after completion
    }
  };

  const handleImageClick = async (actualRow, images) => {
    const hasNoImages = actualRow.image_link.length === 0;
    if (hasNoImages) {
      await handelImageUpload(actualRow.material_no, images);
      await fetchAllStickers();
    } else {
      setCarouselImages(actualRow.image_link);
      setOpenCarousel(true);
    }
  };

  const infoHeadData = [
    t("material_number"),
    t("material_desc"),
    t("p_qty"),
    t("imgs"),
    t("bin_loc"),
    t("bulk_loc"),
    t("Edit"),
    <CButton
      cSrc={<DeleteIcon />}
      cTooltipText="DeleteAll"
      cOnClick={() => handleButtonClick("delete_all")}
    />,
    t("preview"),
    <CButton
      cSrc={<LocalPrintshopIcon />}
      cTooltipText="PrintAll"
      cOnClick={() => handleButtonClick("print_all")}
    />,
  ];

  const infoRowData = [
    {
      value: `material_no`,
      tooltipText: (actualRow) =>
        actualRow.material_status === 0
          ? "MaterialPresent"
          : "MaterialNotPresent",
      status: `material_status`,
      type: "M",
      actualType: (actualRow) => actualRow.tableMode,
      actionV: (value) => handleTableEditClick(AddPrintKeys.MAT_NO_KEY)(value),
    },
    {
      value: `material_desc`,
      type: "T",
      status: `material_status`,
    },
    {
      value: `print_qty`,
      status: `material_status`,
      type: "M",
      eType: "quantity",
      actualType: (actualRow) => actualRow.tableMode,
      actionV: (value) =>
        handleTableEditClick(AddPrintKeys.QUANTITY_KEY)(value),
    },
    {
      value: (actualRow) =>
        actualRow.image_link.length === 0 ? <CameraAltIcon /> : <PreviewIcon />,
      tooltipText: (actualRow) =>
        actualRow.image_link.length === 0 ? "ImageUpload" : "ImageView",
      type: "IU",
      hasImage: (actualRow) =>
        actualRow.image_link.length === 0 ? false : true,
      actionV: (actualRow, images) => handleImageClick(actualRow, images),
    },
    {
      value: `bin_location`,
      status: `material_status`,
      tooltipText: (actualRow) => {
        if (actualRow.other_materials_in_bin_flag == 1) {
          return actualRow.sap_bin_status === 0
            ? "BinPresentDuplicate"
            : "BinNotPresentDuplicate";
        } else {
          return actualRow.sap_bin_status === 0
            ? "BinPresent"
            : "BinNotPresent";
        }
      },
      type: "M",
      actualType: (actualRow) =>
        actualRow.bin_loc_status === 1 ? "T" : actualRow.tableMode,
      actionV: (value) => handleTableEditClick(AddPrintKeys.BIN_LOC_KEY)(value),
    },
    {
      value: `bulk_location`,
      status: `material_status`,
      type: "M",
      actualType: (actualRow) => actualRow.tableMode,
      actionV: (value) =>
        handleTableEditClick(AddPrintKeys.BULK_LOC_KEY)(value),
    },
    {
      // Edit / Save button. CTable2 can't disable it, so show a spinner
      // while busy; handleEditClick ignores clicks during that time.
      value: (actualRow) => {
        if (actualRow.tableMode === "T") return <EditIcon />;
        return isBusy ? <CircularProgress size={20} /> : <SaveIcon />;
      },
      tooltipText: (actualRow) => {
        if (actualRow.tableMode === "T") return t("Edit");
        return isBusy ? "Please wait..." : t("Save");
      },
      type: "IC",
      actionV: (actualRow, index) => handleEditClick(actualRow, index),
    },
    {
      value: <DeleteIcon />,
      tooltipText: "Delete",
      type: "I",
      actionV: (actualRow) => deleteStickerData(actualRow.id),
    },
    {
      value: <PictureAsPdfIcon />,
      tooltipText: "View",
      status: `material_status`,
      type: "I",
      actionV: (actualRow) =>
        pdfPrintView(
          actualRow.id,
          undefined,
          false,
          PrintViewTypes.PRINT_VIEW_ID,
        ),
    },
    {
      value: <LocalPrintshopIcon />,
      tooltipText: "Print",
      status: `material_status`,
      type: "I",
      actionV: (actualRow) =>
        pdfPrintView(
          actualRow.id,
          undefined,
          true,
          PrintViewTypes.PRINT_VIEW_ID,
        ),
    },
  ];

  return {
    poOptions,
    matOptions,
    sizeOptions,
    sLocOptions,
    selectedSize,
    selectedSLoc,
    stickerData,
    handleStickerDataEdit,
    buttonJson,
    labelData,
    handleSizeChange,
    handleStorageLocationChange,
    handleButtonClick,
    infoHeadData,
    infoRowData,
    carouselImages,
    openCarousel,
    setOpenCarousel,
    isPrinting,
    setForceInput,
    forceInput,
    handelUserTyping,
    duplicateDialog,
    handleDuplicateClose,
    isCheckingBin,
    exportEmptyBinLocations,
    handleDuplicateConfirm,
    isBusy,
  };
};

export default PrintingVM;
